import { useEffect, useRef } from 'react';

/**
 * Comportamento padrão dos modais do painel (Fase 30):
 *   - Esc fecha o modal
 *   - Enter aciona o confirmar/salvar (nunca o cancelar)
 *   - Clique no backdrop NÃO fecha — evita perder o que foi digitado
 *     (os overlays não têm mais onClick={onClose})
 *
 * Enter é ignorado quando o foco está em textarea (quebra de linha),
 * select (escolha de opção), botão/link (aciona o próprio elemento) ou
 * quando um input já tratou a tecla com preventDefault (ex: campo de
 * busca por CPF).
 *
 * Modais empilhados (ex: Picker sobre formulário): só o modal do TOPO
 * da pilha responde às teclas — Esc fecha um nível por vez.
 */

const modalStack: symbol[] = [];

export function useModalKeys(
  onClose: () => void,
  onConfirm?: () => void,
  enabled = true,
): void {
  // Refs pra não recriar o listener a cada render (callbacks inline)
  const closeRef = useRef(onClose);
  const confirmRef = useRef(onConfirm);
  closeRef.current = onClose;
  confirmRef.current = onConfirm;

  useEffect(() => {
    if (!enabled) return;
    const id = Symbol('modal');
    modalStack.push(id);

    function handler(e: KeyboardEvent): void {
      if (modalStack[modalStack.length - 1] !== id) return;
      if (e.defaultPrevented) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        closeRef.current();
        return;
      }
      if (e.key === 'Enter' && confirmRef.current) {
        const tag = (e.target as HTMLElement | null)?.tagName;
        if (tag === 'TEXTAREA' || tag === 'SELECT' || tag === 'BUTTON' || tag === 'A') return;
        e.preventDefault();
        confirmRef.current();
      }
    }
    window.addEventListener('keydown', handler);
    return () => {
      const i = modalStack.indexOf(id);
      if (i >= 0) modalStack.splice(i, 1);
      window.removeEventListener('keydown', handler);
    };
  }, [enabled]);
}
